import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { UserPlus, Users as UsersIcon, Pencil, Trash2 } from "lucide-react";

const ROLE_LABELS = { owner: "Owner", admin: "Admin", kasir: "Kasir" };
const ROLE_TONE = {
  owner: "bg-zinc-900 text-white",
  admin: "bg-blue-100 text-blue-800",
  kasir: "bg-emerald-100 text-emerald-800",
};

export default function UsersAdmin() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ email: "", name: "", role: "kasir", password: "" });
  const [editForm, setEditForm] = useState({ name: "", role: "kasir", password: "" });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/users");
      setUsers(r.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal memuat pengguna");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setForm({ email: "", name: "", role: "kasir", password: "" });
    setCreating(true);
  };

  const submitCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", form);
      toast.success(`Pengguna ${form.email} dibuat`);
      setCreating(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal membuat pengguna");
    }
  };

  const openEdit = (u) => {
    setEditing(u);
    setEditForm({ name: u.name || "", role: u.role, password: "" });
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    if (!editing) return;
    const payload = { name: editForm.name, role: editForm.role };
    if (editForm.password) payload.password = editForm.password;
    try {
      await api.patch(`/users/${editing.id}`, payload);
      toast.success(`Pengguna ${editing.email} diperbarui`);
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal memperbarui");
    }
  };

  const removeUser = async (u) => {
    if (!window.confirm(`Hapus pengguna ${u.email}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Pengguna dihapus");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menghapus");
    }
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="users-admin-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-zinc-900 text-white">
            <UsersIcon className="h-4 w-4" />
          </span>
          <div>
            <h1 className="text-2xl sm:text-3xl font-display font-bold text-zinc-900">Kelola Pengguna</h1>
            <p className="text-sm text-zinc-500 mt-0.5">
              Tambah anggota tim &amp; atur peran akses mereka.
            </p>
          </div>
        </div>
        <Button
          data-testid="btn-create-user"
          onClick={openCreate}
          className="bg-zinc-900 hover:bg-zinc-800"
        >
          <UserPlus className="h-4 w-4 mr-1.5" /> Tambah Pengguna
        </Button>
      </div>

      <Card className="border-zinc-200 shadow-none rounded-md">
        <div className="p-4 border-b border-zinc-200 text-xs text-zinc-500">
          {users.length} pengguna terdaftar
        </div>
        <Table>
          <TableHeader className="bg-zinc-50">
            <TableRow>
              <TableHead>Email</TableHead>
              <TableHead>Nama</TableHead>
              <TableHead className="w-[110px]">Role</TableHead>
              <TableHead className="w-[110px] text-right">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-8 text-zinc-500">
                  Memuat…
                </TableCell>
              </TableRow>
            )}
            {!loading && users.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-8 text-zinc-500">
                  Belum ada pengguna.
                </TableCell>
              </TableRow>
            )}
            {users.map((u) => {
              const isMe = me?.id === u.id;
              return (
                <TableRow key={u.id} data-testid={`user-row-${u.email}`}>
                  <TableCell className="font-medium">
                    {u.email}
                    {isMe && (
                      <span className="ml-2 text-[10px] uppercase tracking-widest text-zinc-400">
                        (anda)
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-zinc-600">{u.name || "—"}</TableCell>
                  <TableCell>
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] uppercase tracking-widest font-medium ${
                        ROLE_TONE[u.role] || "bg-zinc-100 text-zinc-700"
                      }`}
                    >
                      {ROLE_LABELS[u.role] || u.role}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => openEdit(u)}
                        data-testid={`btn-edit-user-${u.email}`}
                        className="h-8 w-8"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => removeUser(u)}
                        disabled={isMe}
                        data-testid={`btn-del-user-${u.email}`}
                        className="h-8 w-8 text-rose-600 hover:text-rose-700 hover:bg-rose-50 disabled:opacity-30"
                        title={isMe ? "Tidak bisa menghapus akun sendiri" : "Hapus"}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>

      {/* Create dialog */}
      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="max-w-md" data-testid="create-user-dialog">
          <DialogHeader>
            <DialogTitle>Tambah Pengguna Baru</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitCreate} className="space-y-3">
            <div>
              <Label>Email</Label>
              <Input
                data-testid="create-user-email"
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>
            <div>
              <Label>Nama</Label>
              <Input
                data-testid="create-user-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div>
              <Label>Password (min 6)</Label>
              <Input
                data-testid="create-user-password"
                type="password"
                required
                minLength={6}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
              />
            </div>
            <div>
              <Label>Role</Label>
              <Select
                value={form.role}
                onValueChange={(v) => setForm({ ...form, role: v })}
              >
                <SelectTrigger data-testid="create-user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="kasir">Kasir — hanya Faktur &amp; Dashboard</SelectItem>
                  <SelectItem value="admin">Admin — semua kecuali kelola pengguna</SelectItem>
                  <SelectItem value="owner">Owner — akses penuh</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreating(false)}>
                Batal
              </Button>
              <Button type="submit" data-testid="submit-create-user" className="bg-zinc-900 hover:bg-zinc-800">
                Simpan
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-md" data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle>Ubah Pengguna {editing?.email}</DialogTitle>
          </DialogHeader>
          {editing && (
            <form onSubmit={submitEdit} className="space-y-3">
              <div>
                <Label>Nama</Label>
                <Input
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                />
              </div>
              <div>
                <Label>Role</Label>
                <Select
                  value={editForm.role}
                  onValueChange={(v) => setEditForm({ ...editForm, role: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="kasir">Kasir</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="owner">Owner</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Password Baru (opsional)</Label>
                <Input
                  type="password"
                  minLength={6}
                  value={editForm.password}
                  onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                  placeholder="Biarkan kosong bila tidak diganti"
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditing(null)}>
                  Batal
                </Button>
                <Button type="submit" data-testid="submit-edit-user" className="bg-zinc-900 hover:bg-zinc-800">
                  Simpan Perubahan
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
